import { supabase } from '../supabase.js';

const ACTIVE_KEY = 'bb_active_workspace_id';

export const workspace = $state({
  loaded: false,
  id: null,
  name: 'My Model',
  partner_id: null,
  overall_context: null,
  audiences: [],
  deals: [],
  steps: [],
  chains: [],
  connections: [],
  pairings: [],
  insights: [],
  plans: [],
  quota: null
});

export async function loadWorkspace(targetWorkspaceId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data: profile } = await supabase
    .from('blv_profiles')
    .select('id')
    .eq('auth_user_id', user.id)
    .maybeSingle();
  if (!profile) return;

  // Determine which workspace to load. A workspace belongs to a company (its HQ team shares it) or, with no
  // company, to one person — RLS decides which ones this person may open (fn_bb_my_workspaces lists them).
  let wsId = targetWorkspaceId || (typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_KEY) : null);

  let ws = null;
  if (wsId) {
    const { data } = await supabase.from('bb_workspaces').select('*').eq('id', wsId).maybeSingle();
    ws = data;
  }
  if (!ws) {
    const { data: rows } = await supabase.rpc('fn_bb_my_workspaces');
    const list = rows || [];
    const pick = list.find(r => r.has_workspace && r.partner_id) || list.find(r => r.has_workspace);
    const company = list.find(r => !r.has_workspace);
    if (pick) {
      const { data } = await supabase.from('bb_workspaces').select('*').eq('id', pick.workspace_id).maybeSingle();
      ws = data;
    } else {
      // First visit: your company's workspace if you have a company, else a personal one.
      const { data: newWs } = await supabase
        .from('bb_workspaces')
        .insert(company
          ? { owner_profile_id: profile.id, partner_id: company.partner_id, name: company.name, is_default: true }
          : { owner_profile_id: profile.id, name: 'My Model', is_default: true })
        .select()
        .single();
      ws = newWs;
    }
  }

  if (!ws) return;

  workspace.id = ws.id;
  workspace.name = ws.name;
  workspace.partner_id = ws.partner_id ?? null;
  workspace.overall_context = ws.overall_context ?? null;
  if (typeof localStorage !== 'undefined') localStorage.setItem(ACTIVE_KEY, ws.id);

  const wsIdFinal = ws.id;
  const profileId = profile.id;

  // A company design shows that company's HQ offers: make sure each has its builder row (fn_bb_sync_offers).
  if (ws.partner_id) await supabase.rpc('fn_bb_sync_offers', { p_workspace_id: wsIdFinal });

  const [
    { data: audiences },
    { data: deals },
    { data: steps },
    { data: chains },
    { data: connections },
    { data: pairings },
    { data: insights },
    { data: plans },
    quotaRes
  ] = await Promise.all([
    supabase.from('bb_audiences').select('*').eq('workspace_id', wsIdFinal).is('deleted_at', null).order('created_at', { ascending: false }),
    supabase.from('bb_deals').select('*, offer:map_offers(id, name, status, promise_text, value_text, how_text, ask_type, ask_note, track_key)').eq('workspace_id', wsIdFinal).is('deleted_at', null).order('created_at', { ascending: false }),
    supabase.from('bb_steps').select('*').eq('workspace_id', wsIdFinal).is('deleted_at', null).order('created_at', { ascending: true }),
    supabase.from('bb_chains').select('*').eq('workspace_id', wsIdFinal).order('created_at', { ascending: false }),
    supabase.from('bb_segment_connections').select('*').eq('workspace_id', wsIdFinal),
    supabase.from('bb_pairings').select('*'),
    supabase.from('bb_insights').select('*').eq('workspace_id', wsIdFinal).is('deleted_at', null).order('created_at', { ascending: false }),
    supabase.from('bb_plans').select('*').eq('workspace_id', wsIdFinal).is('deleted_at', null).order('created_at', { ascending: false }),
    supabase.from('bb_user_quota').select('*').eq('owner_profile_id', profileId).maybeSingle()
  ]);

  workspace.audiences = audiences || [];
  // "deals" is the builder's word for its offer rows (the table is bb_deals). A retired HQ offer drops out.
  workspace.deals = (deals || [])
    .filter(d => d.offer?.status !== 'retired')
    .map(d => (d.offer ? { ...d, name: d.offer.name } : d));
  workspace.steps = steps || [];
  workspace.connections = connections || [];
  workspace.insights = insights || [];
  workspace.plans = plans || [];
  workspace.pairings = pairings || [];
  workspace.quota = quotaRes?.data || null;

  workspace.chains = (chains || []).map(c => {
    const chainConns = (connections || []).filter(cn => cn.chain_id === c.id);
    const chainStepIds = new Set();
    chainConns.forEach(cn => {
      if (cn.from_segment_kind === 'step') chainStepIds.add(cn.from_segment_id);
      if (cn.to_segment_kind === 'step') chainStepIds.add(cn.to_segment_id);
    });
    return {
      ...c,
      steps: (steps || []).filter(s => chainStepIds.has(s.id)),
      connections: chainConns,
      pairing: (pairings || []).find(p => p.chain_id === c.id) || null
    };
  });

  workspace.loaded = true;
}

export async function refreshWorkspace(targetWorkspaceId) {
  await loadWorkspace(targetWorkspaceId);
}

export async function callBbAi(action, payload = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');

  const res = await fetch('https://kbbxywhzfejvmsngaivy.supabase.co/functions/v1/bb-ai', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ action, ...payload })
  });

  if (res.status === 402) {
    // Out of credits (Miles 2026-11-05: the builder runs on credits; the first business build is free once).
    if (typeof window !== 'undefined') window.open('https://bestlifeventures.com/credits.html', '_blank', 'noopener');
    throw new Error("You're out of credits — buy more at bestlifeventures.com/credits");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `bb-ai ${action} failed (${res.status})`);
  }
  return await res.json();
}

export async function brainstormTurn(messages) {
  return await callBbAi('brainstorm_turn', { messages });
}
